// Přihlášení / registrace / obnova hesla (issue #17, screens/07-prihlaseni.md
// + m07-prihlaseni.md).
//
// Restyle nativních Shoptet stránek `/login/`, `/registrace/`
// a `/klient/zapomenute-heslo/` — žádný vlastní formulář, žádný vlastní submit:
//   • layout `1fr 484px` = tmavý benefit panel + formulářová karta (na mobilu
//     formulář první, panel pod ním — m07 §„Pořadí na mobilu")
//   • segmentový přepínač = ODKAZY mezi /login/ a /registrace/ (Shoptet má
//     oddělené stránky), v obnově hesla se nestaví
//   • pole dostávají ikonu uvnitř vlevo (obalový `span.bs-auth__ic`) a heslo
//     přepínač zobrazení (`passwordToggle.js`)
//   • registrace: nativní `customerGroupId` radia → karty typu zákazníka,
//     nativní `companyShopping` odkrývá firemní pole (default JS, my jen stylujeme)
//
// Co se do implementace ZÁMĚRNĚ nedostalo (nativní formulář to nemá a vymýšlet
// nefunkční prvky nechceme — detail v .claude/night-notes.md):
//   • „Zůstat přihlášen" — přihlašovací formulář Shoptetu pole nemá
//   • souhlas jako checkbox — souhlas je u Shoptetu implicitní (`consents[]`
//     je hidden input + text), takže CTA nemá co odemykat
//   • Jméno + Příjmení ve dvou sloupcích — nativní pole je jedno (`billFullName`)
//     a objeví se až ve firemním/plném profilu
//
// Idempotentní (`html.bs-auth-ready`, `data-bs-auth`).

import { icon } from './icon.js';
import { attachPasswordToggle } from './passwordToggle.js';
// jeden zdroj cisel hodnoceni pro kartu i sekci `.bs-rv` (zadani 2026-09-10)
import { loadReviews, reviewSummary } from './reviews.js';
import { bodyClass, page } from './pages.js';
import L from '../locale/cs/authPage.js';

// --- copy ------------------------------------------------------------------
// Nadpis: nad H1 stojí segmentový přepínač s popiskem „Přihlášení"/„Registrace",
// takže nativní H1 je duplicita — design ho nahrazuje oslovením. Na obnově hesla
// přepínač není, tam nativní H1 zůstává. Texty jsou v locale, tady jen mapa na
// odkazy shopu (`page()`), které se liší mezi jazyky.
const HEAD = L.head;

const TABS = [
  { key: 'login', label: L.tabs.login, href: page('login') },
  { key: 'register', label: L.tabs.register, href: page('register') },
];

const SWITCH = {
  login: { text: L.switchText.login.text, link: L.switchText.login.link, href: page('register') },
  register: { text: L.switchText.register.text, link: L.switchText.register.link, href: page('login') },
};

const LABEL_EMAIL = L.labelEmail;
const LABEL_PASSWORD = L.labelPassword;
const LABEL_FORGOT = L.labelForgot;
const LABEL_BACK = L.labelBack;

// Názvy skupin zákazníků jsou z administrace; popis pod nimi je náš, ale jen
// tam, kde nativní label vlastní popis nenese (07-prihlaseni.md §„Registrace").
const TYPE_DESC = L.typeDesc;
const B2B_NOTE = L.b2bNote;

// Benefit panel (07-prihlaseni.md §„Levý benefit panel"). Čísla trust karty jsou
// zatím z designu — stejný zdroj jako panel hodnocení na homepage; před go-live
// napojit na reálné hodnocení obchodu (issue #41).
const PANEL = L.panel;

const BENEFITS = L.benefits;

// Trust karta panelu. Čísla NEJSOU konstanty — skóre i počet hodnocení jdou
// z `reviewSummary()` (reviews.js), tedy ze stejného zdroje jako sekce
// „Ověřeno zákazníky" v patičce téže stránky. Do 2026-09-10 tu stály zapsané
// hodnoty a karta hlásila 4,8/5 a 3 012 recenzí, zatímco sekce o kus níž
// počítala 4,6/5 a 23 783 hodnocení. Od recenze U2 jdou čísla platforem denně
// z `/reviews/summary`: karta se postaví ze zálohy v manifestu a po dorazu živých
// dat (`loadReviews()`, týž dotaz jako patička) přepíše skóre, hvězdy i počet.
// Tady zůstávají jen slova kolem čísel.
const TRUST = L.trust;

// --- helpery ---------------------------------------------------------------
function node(tag, className, text) {
  const n = document.createElement(tag);
  if (className) n.className = className;
  if (text != null) n.textContent = text;
  return n;
}

function link(href, className, text) {
  const a = node('a', className, text);
  a.href = href;
  return a;
}

// Hvězdy dvouvrstvou technikou (_stars.scss) — stejný markup jako homepage.js.
function stars(percent) {
  const wrap = node('span', 'bs-stars bs-stars--sm');
  wrap.setAttribute('aria-hidden', 'true');
  wrap.appendChild(document.createTextNode('★★★★★'));
  const fill = node('span', 'bs-stars__fill', '★★★★★');
  fill.style.setProperty('--bs-stars-pct', percent);
  wrap.appendChild(fill);
  return wrap;
}

// Obalí pole do `span.bs-auth__ic` (ikona vlevo uvnitř, prostor pro přepínač hesla).
function decorate(input, iconName) {
  if (!input) return null;
  if (input.dataset.bsAuthIc) return input.parentElement;
  input.dataset.bsAuthIc = '1';
  const wrap = node('span', 'bs-auth__ic');
  input.parentNode.insertBefore(wrap, input);
  if (iconName) wrap.appendChild(icon(iconName, 'bs-auth__ic-i'));
  wrap.appendChild(input);
  return wrap;
}

// Přihlašovací formulář má pole bez labelu (jen placeholder) — label dodáme.
function ensureLabel(input, text, fallbackId) {
  if (!input) return null;
  const group = input.closest('.form-group') || input.parentElement;
  if (!group) return null;
  const existing = group.querySelector('label');
  if (existing) {
    existing.classList.add('bs-auth__lab');
    return existing;
  }
  if (!input.id) input.id = fallbackId;
  const lab = node('label', 'bs-auth__lab', text);
  lab.setAttribute('for', input.id);
  group.insertBefore(lab, group.firstChild);
  return lab;
}

function buildSwitch(view, form, nativeLink) {
  const cfg = SWITCH[view];
  if (!cfg || !form || form.querySelector('.bs-auth__switch')) return;
  const box = node('div', 'bs-auth__switch');
  box.appendChild(document.createTextNode(cfg.text + ' '));
  const a = nativeLink || link(cfg.href, null, cfg.link);
  a.className = 'bs-auth__switch-link';
  a.textContent = cfg.link;
  if (!a.getAttribute('href')) a.href = cfg.href;
  box.appendChild(a);
  form.appendChild(box);
}

// --- benefit panel ---------------------------------------------------------
function buildPanel() {
  const panel = node('aside', 'bs-auth__panel');
  panel.appendChild(icon('user', 'bs-auth__deco'));

  const body = node('div', 'bs-auth__panel-in');
  body.appendChild(node('span', 'bs-auth__eyebrow', PANEL.eyebrow));
  body.appendChild(node('h2', 'bs-auth__panel-title', PANEL.title));

  const list = node('div', 'bs-auth__benefits');
  for (const b of BENEFITS) {
    const row = node('div', 'bs-auth__benefit');
    const ic = node('span', 'bs-auth__benefit-ic');
    ic.appendChild(icon(b.icon));
    const txt = node('span', 'bs-auth__benefit-txt');
    txt.appendChild(node('strong', null, b.title));
    txt.appendChild(node('span', null, b.text));
    row.appendChild(ic);
    row.appendChild(txt);
    list.appendChild(row);
  }
  body.appendChild(list);

  // bez jediné platformy s čísly se karta nekreslí — stejné pravidlo jako
  // u sekce `.bs-rv`, radši nic než vymyšlené skóre
  const rv = reviewSummary();
  if (rv) {
    const trust = node('div', 'bs-auth__trust');
    const score = node('span', 'bs-auth__trust-score');
    const num = node('strong', null, rv.score + rv.max);
    score.appendChild(num);
    const st = stars(rv.percent);
    score.appendChild(st);
    trust.appendChild(score);
    trust.appendChild(node('span', 'bs-auth__trust-sep'));
    const txt = node('span', 'bs-auth__trust-text');
    txt.appendChild(document.createTextNode(TRUST.before));
    txt.appendChild(node('b', null, rv.customers));
    txt.appendChild(document.createTextNode(TRUST.between));
    const count = node('b', null, rv.count);
    txt.appendChild(count);
    txt.appendChild(document.createTextNode(TRUST.after));
    trust.appendChild(txt);
    body.appendChild(trust);
    loadReviews().then(function (live) {
      const next = live && reviewSummary(live.rows);
      if (!next) return;
      num.textContent = next.score + next.max;
      st.querySelector('.bs-stars__fill').style.setProperty('--bs-stars-pct', next.percent);
      count.textContent = next.count;
    });
  }

  panel.appendChild(body);
  return panel;
}

// --- kostra stránky --------------------------------------------------------
function buildShell(view) {
  const wrapper = document.querySelector('#content-wrapper .content-wrapper-in');
  const main = document.querySelector('#content-wrapper #content') || document.getElementById('content');
  if (!wrapper || !main) return null;

  wrapper.classList.add('bs-auth');
  main.classList.add('bs-auth__card');
  if (!wrapper.querySelector('.bs-auth__panel')) {
    wrapper.insertBefore(buildPanel(), wrapper.firstChild);
  }

  const host = main.querySelector('.content-inner') || main;
  host.classList.add('bs-auth__card-in');

  // segmentový přepínač = odkazy mezi oddělenými stránkami Shoptetu
  if (HEAD[view] && !host.querySelector('.bs-auth__tabs')) {
    const tabs = node('div', 'bs-auth__tabs');
    for (const t of TABS) {
      const a = link(t.href, 'bs-auth__tab' + (t.key === view ? ' is-active' : ''), t.label);
      if (t.key === view) a.setAttribute('aria-current', 'page');
      tabs.appendChild(a);
    }
    host.insertBefore(tabs, host.firstChild);
  }

  const h1 = host.querySelector('h1');
  if (h1 && !h1.dataset.bsAuth) {
    h1.dataset.bsAuth = '1';
    h1.classList.add('bs-auth__title');
    const copy = HEAD[view];
    if (copy) {
      h1.textContent = copy.title;
      h1.insertAdjacentElement('afterend', node('p', 'bs-auth__sub', copy.sub));
    }
  }
  return host;
}

// --- /login/ ---------------------------------------------------------------
function initLogin(host) {
  const form = document.getElementById('formLogin');
  if (!form) return;

  const email = form.querySelector('input[name="email"]');
  ensureLabel(email, LABEL_EMAIL, 'bs-auth-email');
  decorate(email, 'mail');

  const pass = form.querySelector('input[name="password"]');
  const passLab = ensureLabel(pass, LABEL_PASSWORD, 'bs-auth-password');
  attachPasswordToggle(pass, decorate(pass, 'lock'));

  // Nativní blok pomocných odkazů (pod tlačítkem) rozdělíme: obnova hesla nad
  // pole hesla, registrace do přepínací věty na konci — odkazy PŘESOUVÁME,
  // ne kopírujeme.
  // POZOR (DR U7, 2026-08-30): Shoptet s příznakem `ums_forms_redesign--on`
  // přejmenoval `.password-helper` na `p.alternative-links` — stará kotva tiše
  // přestala trefovat, „Zapomenuté heslo?" se nepřesouvalo nad pole a nativní
  // dvojice odkazů zůstávala nenastylovaná pod formulářem (ověřeno živě na dev).
  // Držíme obě varianty, ať fixture i případný rollback platformy fungují.
  const helper = form.querySelector('.password-helper, p.alternative-links');
  let signup = null;
  if (helper) {
    const forgot = helper.querySelector('a[href*="' + page('forgotPassword') + '"]');
    if (forgot && passLab && passLab.parentNode) {
      forgot.className = 'bs-auth__forgot';
      forgot.textContent = LABEL_FORGOT;
      const row = node('div', 'bs-auth__lab-row');
      passLab.parentNode.insertBefore(row, passLab);
      row.appendChild(passLab);
      row.appendChild(forgot);
    }
    signup = helper.querySelector('a[data-testid="signup"], a[href="' + page('register') + '"]');
    if (!helper.querySelector('a')) helper.remove();
  }

  // Wrapper `.login-wrapper` Shoptet z formuláře odstranil (ověřeno na dev
  // 2026-08-09) a scoped selektor tiše přestal trefovat → tlačítko zůstávalo
  // nenastylované. Ve formuláři je právě jeden submit, takže scope netřeba
  // (stejně to řeší i větev zapomenutého hesla).
  const cta = form.querySelector('button[type="submit"], input[type="submit"]');
  if (cta) {
    cta.classList.add('bs-auth__cta');
    if (cta.tagName === 'BUTTON' && !cta.querySelector('.bs-icon')) cta.appendChild(icon('arrow-right'));
  }

  const social = form.querySelector('.social-login-buttons');
  if (social) social.classList.add('bs-auth__social');

  buildSwitch('login', form, signup);
  void host;
}

// --- /registrace/ ----------------------------------------------------------
function initRegister(host) {
  const form = document.getElementById('register-form');
  if (!form) return;

  // 1) typ zákazníka — nativní radia `customerGroupId` jako karty
  const firstGroup = form.querySelector('input[name="customerGroupId"]');
  const types = firstGroup ? firstGroup.closest('fieldset') : null;
  if (types && !types.dataset.bsAuth) {
    types.dataset.bsAuth = '1';
    types.classList.add('bs-auth__types');

    const cards = [];
    for (const input of types.querySelectorAll('input[name="customerGroupId"]')) {
      const group = input.closest('.form-group');
      if (!group) continue;
      group.classList.add('bs-auth__type');
      const lab = group.querySelector('label');
      if (lab) {
        lab.classList.add('bs-auth__type-lab');
        const strong = lab.querySelector('strong');
        let desc = '';
        for (const child of Array.from(lab.childNodes)) {
          if (child === strong) continue;
          desc += child.textContent || '';
          child.remove();
        }
        desc = desc.replace(/\s+/g, ' ').replace(/^[\s(-]+/, '').replace(/[\s)]+$/, '');
        if (!desc) {
          desc = input.classList.contains('is-wholesale') ? TYPE_DESC.wholesale : TYPE_DESC.retail;
        }
        lab.appendChild(node('span', 'bs-auth__type-desc', desc));
      }
      cards.push({ input: input, group: group });
    }

    const note = node('p', 'bs-auth__b2b-note', B2B_NOTE);
    types.appendChild(note);

    const sync = function () {
      let b2b = false;
      for (const c of cards) {
        c.group.classList.toggle('is-on', c.input.checked);
        if (c.input.checked && c.input.classList.contains('is-wholesale')) b2b = true;
      }
      note.classList.toggle('is-on', b2b);
    };
    for (const c of cards) c.input.addEventListener('change', sync);
    sync();
  }

  // 2) pole: ikona vlevo + přepínač u hesel
  decorate(form.querySelector('#email, input[name="email"]'), 'mail');
  for (const pass of form.querySelectorAll('input[type="password"]')) {
    attachPasswordToggle(pass, decorate(pass, 'lock'));
  }

  // 3) firemní blok — odkrývá ho default JS (`data-unveil`), my jen stylujeme
  const additional = document.getElementById('additionalInformation');
  if (additional) additional.classList.add('bs-auth__more');
  const companyInfo = document.getElementById('company-info');
  if (companyInfo) companyInfo.classList.add('bs-auth__company');
  const companyRow = form.querySelector('.form-group.company-shopping');
  if (companyRow) companyRow.classList.add('bs-auth__check');
  const newsletter = form.querySelector('.stay-in-touch .form-group');
  if (newsletter) newsletter.classList.add('bs-auth__check');
  const consents = form.querySelector('.form-group.consents');
  if (consents) consents.classList.add('bs-auth__fine');

  // nativní nápověda k DIČ visí ZA polem (vlastní řádek s osamělým „?") → k labelu
  const tipGroup = form.querySelector('.form-group-hasTooltip');
  if (tipGroup) {
    const tip = tipGroup.querySelector('.form-group-tooltip');
    const lab = tipGroup.querySelector('label');
    if (tip && lab && tip.parentNode !== lab) lab.appendChild(tip);
  }

  const cta = form.querySelector('.submit-wrapper input[type="submit"], .submit-wrapper button[type="submit"]');
  if (cta) cta.classList.add('bs-auth__cta');

  // 4) registrace přes sociální sítě = alternativa k CTA → na konec karty
  // ⚠️ Obal se na /registrace/ jmenuje `.social-login` (nadpis H2 + dvě
  // `.form-group` s `a.login-btn`), NE `.social-login-buttons` jako na
  // /login/ — a `.register-soc` v živém DOM neexistuje vůbec (nález
  // 2026-09-10: tlačítka „Registrovat přes Google/Seznam" zůstávala holými
  // odkazy 175x21, zatímco na přihlášení jedou jako pilulky 414x48).
  // Bereme všechny tři tvary, ať to přežije i další přejmenování.
  const soc = form.querySelector('.register-soc, .social-login, .social-login-buttons');
  if (soc && !soc.dataset.bsAuth) {
    soc.dataset.bsAuth = '1';
    soc.classList.add('bs-auth__social');
    const col = soc.closest('.col-md-4') || soc;
    form.appendChild(col);
  }

  buildSwitch('register', form, null);
  void host;
}

// Vysvětlující odstavec těsně před formulářem (nativní, bez třídy). Vlastní
// `.bs-auth__sub` z `buildShell` nebereme — ten už podtitulek je.
function prevParagraph(form) {
  const p = form.previousElementSibling;
  if (!p || p.tagName !== 'P') return null;
  if (p.classList.contains('bs-auth__sub')) return null;
  return txtOf(p) ? p : null;
}

function txtOf(n) {
  return n ? (n.textContent || '').replace(/\s+/g, ' ').trim() : '';
}

// --- /klient/zapomenute-heslo/ ---------------------------------------------
function initForgot(host) {
  const form = document.getElementById('formForgottenPassword');
  if (!form || !host) return;

  const h1 = host.querySelector('h1');
  // Nativní vysvětlující odstavec → pod nadpis jako podtitulek.
  // POZOR (DR U7, 2026-08-30): s `ums_forms_redesign--on` už NEleží uvnitř
  // fieldsetu, ale je SOUROZENCEM před `<form>` — stará kotva netrefila a věta
  // „Zadejte e-mailovou adresu…" zůstávala nenastylovaná nad ikonou klíče
  // (ověřeno živě na dev). Bereme obě polohy.
  const p = form.querySelector('fieldset > p') || prevParagraph(form);
  if (h1 && p && !p.dataset.bsAuth) {
    p.dataset.bsAuth = '1';
    p.classList.add('bs-auth__sub');
    h1.insertAdjacentElement('afterend', p);
  }

  if (h1 && !host.querySelector('.bs-auth__key')) {
    const key = node('span', 'bs-auth__key');
    key.appendChild(icon('key'));
    h1.insertAdjacentElement('beforebegin', key);
    const back = link(page('login'), 'bs-auth__back', LABEL_BACK);
    back.insertBefore(icon('arrow-left'), back.firstChild);
    key.insertAdjacentElement('beforebegin', back);
  }

  decorate(form.querySelector('input[type="email"], #email'), 'mail');
  const cta = form.querySelector('input[type="submit"], button[type="submit"]');
  if (cta) cta.classList.add('bs-auth__cta');
}

// --- init ------------------------------------------------------------------
function detectView() {
  const b = document.body.classList;
  const path = window.location.pathname;
  // body třída i cesta z manifestu shopu (SK `/registracia/`, `/klient/zabudnute-heslo/`)
  if (b.contains(bodyClass('login')) || path.indexOf(page('login')) === 0) return 'login';
  if (b.contains(bodyClass('register')) || path.indexOf(page('register')) === 0) return 'register';
  if (b.contains(bodyClass('forgotPassword')) || path.indexOf(page('forgotPassword')) === 0) return 'forgot';
  return null;
}

export function initAuthPage() {
  const view = detectView();
  if (!view) return;
  // stránka musí nést odpovídající nativní formulář (jinak je uživatel přihlášený
  // a Shoptet přesměroval jinam)
  const forms = { login: 'formLogin', register: 'register-form', forgot: 'formForgottenPassword' };
  if (!document.getElementById(forms[view])) return;

  const host = buildShell(view);
  if (!host) return;

  if (view === 'login') initLogin(host);
  else if (view === 'register') initRegister(host);
  else initForgot(host);

  document.documentElement.classList.add('bs-auth-ready');
}
